import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { EmptyState, FormFiltersBackground, PageMain } from "components/molecules";
import { QUERIES_KEYS } from "queries";
import { SurveyCoursesReportRow, reportsApi } from "api/reportsApi";
import { PaziaDateSelect, PaziaSelect, Stars } from "components/atoms";
import { TanTableClient, useFilters, TanTableColumn } from "components/molecules";
import { formatDate } from "utils";
import { PaziaBreadcrumbs } from "components/atoms";
import { routes } from "consts";
import { useNavigate } from "react-router-dom";
import { REPORTS_IDS, REPORTS_NAMES } from "consts/reports";
import { CourseOption, useCoursesOptions } from "api/hooks";
import { CourseApi, CourseData } from "api";

export const SurveysCourse = () => {
  const navigate = useNavigate();
  const { filter, changeFilter } = useFilters();
  const [selectedCourse, setSelectedCourse] = useState<CourseData | null>(null);
  const { isLoading: loadingCourses, options } = useCoursesOptions();
  const { data, isLoading, refetch } = useQuery({
    queryKey: QUERIES_KEYS.REPORTS.SURVEYS_COURSES(),
    queryFn: () => reportsApi.getSurveysCourseReport(filter, selectedCourse?.id!),
    enabled: selectedCourse != null,
  });

  useEffect(() => {
    refetch();
  }, [filter]);

  const columns: TanTableColumn<SurveyCoursesReportRow>[] = [
    {
      id: "userEmail",
      accessorKey: "userEmail",
      header: "Użytkownik",
      size: 200,
    },
    {
      id: "createdDate",
      accessorKey: "createdDate",
      header: "Utworzono",
      cell: (info) => formatDate(info.getValue<Date>(), true, true),
    },
    {
      id: "rating",
      accessorKey: "rating",
      header: "Ocena",
      cell: (info) => {
        const rating = info.getValue<number>();
        return (
          <div className="flex items-center gap-2">
            <span className="font-semibold"> {rating} / 5</span>
            <Stars numberOfStars={rating} />
          </div>
        );
      },
    },
    {
      id: "comment",
      accessorKey: "comment",
      header: "Komentarz",
    },
  ];

  const onFromDateChange = (date: Date | null) =>
    changeFilter("from", date ? `createdDate ge ${date.toISOString()}` : "");

  const onToDateChange = (date: Date | null) => changeFilter("to", date ? `createdDate le ${date.toISOString()}` : "");

  const tableData = data?.data ?? [];
  return (
    <PageMain>
      <PaziaBreadcrumbs
        segments={[{ title: "Raporty", path: routes.REPORTS }, { title: REPORTS_NAMES[REPORTS_IDS.SURVEYS_COURSES] }]}
      />
      <FormFiltersBackground>
        <PaziaSelect
          isLoading={loadingCourses}
          options={options}
          name="courseId"
          label="Kurs"
          required
          onChange={(x: CourseOption) => setSelectedCourse(x?.course ?? null)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <PaziaDateSelect name="dateFrom" label="Data od" onChange={onFromDateChange} />
          <PaziaDateSelect name="dateTo" label="Data od" onChange={onToDateChange} />
        </div>
      </FormFiltersBackground>
      <div>
        {selectedCourse == null ? (
          <EmptyState title="Brak kursu" description="Wybierz kurs aby załadować raport ocen końcowych" />
        ) : (
          <TanTableClient data={tableData} columns={columns} />
        )}
      </div>
    </PageMain>
  );
};
